import { collectionGroup, endBefore, getCountFromServer, getDocs, limit, limitToLast, query, startAfter, where, orderBy } from 'firebase/firestore';
import { toLower } from 'lodash';
import { isBooleanTrue, isListNotEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from '../utils/objectUtils';
import AuthUser from './AuthUser';
import { toE164PhoneNumber, toPhoneNumberLocal } from '../utils/stringFormatter';
import { db } from '../firebase';
import Address from './Address';
import { subCollectionCreate, subCollectionGetByParentIdAndSubId, subCollectionGetBySingleQuery, subCollectionRemove, subCollectionUpdate } from '../utils/dbUtils';

class AffiliateUser {
	static collectionName = 'affiliates';
	static subCollectionName = 'affiliateUsers';

	affiliateId = null;
	userId = null;

	email = null;
	phone = null;

	firstName = null;
	firstNameLower = null;
	middleName = null;
	lastName = null;
	lastNameLower = null;
	suffix = null;

	billingAddress = new Address(null).toDto();
	shippingAddress = new Address(null).toDto();

	roles = ['ADMIN'];

	phoneVerified = false;

	constructor(affiliateUser) {
		if (isObjectNotEmpty(affiliateUser)) {
			if (isStringNotEmpty(affiliateUser.affiliateId)) { this.affiliateId = toLower(affiliateUser.affiliateId); }
			if (isStringNotEmpty(affiliateUser.userId)) { this.userId = affiliateUser.userId; }

			if (isStringNotEmpty(affiliateUser.email)) { this.email = toLower(affiliateUser.email); }
			if (isStringNotEmpty(affiliateUser.phone)) { this.phone = toE164PhoneNumber(affiliateUser.phone); }

			if (isStringNotEmpty(affiliateUser.firstName)) {
				this.firstName = affiliateUser.firstName;
				this.firstNameLower = toLower(affiliateUser.firstName);
			}
			if (isStringNotEmpty(affiliateUser.lastName)) {
				this.lastName = affiliateUser.lastName;
				this.lastNameLower = toLower(affiliateUser.lastName);
			}
			if (isStringNotEmpty(affiliateUser.middleName)) { this.middleName = affiliateUser.middleName; }
			if (isStringNotEmpty(affiliateUser.suffix)) { this.suffix = affiliateUser.suffix; }

			if (isObjectNotEmpty(affiliateUser.billingAddress)) { this.billingAddress = new Address(affiliateUser.billingAddress).toDto(); }
			if (isObjectNotEmpty(affiliateUser.shippingAddress)) { this.shippingAddress = new Address(affiliateUser.shippingAddress).toDto(); }

			if (isListNotEmpty(affiliateUser.roles)) { this.roles = affiliateUser.roles; }

			if (isBooleanTrue(affiliateUser.phoneVerified)) { this.phoneVerified = true; }
		}
	}

	toDto = () => {
		return {
			affiliateId: this.affiliateId,
			userId: this.userId,

			email: this.email,
			phone: this.phone,

			firstName: this.firstName,
			firstNameLower: this.firstNameLower,
			middleName: this.middleName,
			lastName: this.lastName,
			lastNameLower: this.lastNameLower,
			suffix: this.suffix,

			billingAddress: this.billingAddress,
			shippingAddress: this.shippingAddress,

			roles: this.roles,

			phoneVerified: this.phoneVerified,
		};
	}

	isValid = () => {
		if (isStringEmpty(this.userId)) {
			throw new Error('userId is required.');
		}

		if (isStringEmpty(this.affiliateId)) {
			throw new Error('affiliateId is required.');
		}

		return true;
	}

	create = async () => {
		if (this.isValid()) {
			const existingAffiliateUser = await AffiliateUser.getByAffiliateIdAndUserId(this.affiliateId, this.userId);
			if (isObjectNotEmpty(existingAffiliateUser)) {
				const success = await AuthUser.updateAffiliateClaims(this.affiliateId, this.userId, this.roles);
				if (isBooleanTrue(success)) {
					return { ...existingAffiliateUser, roles: this.roles };
				}
			} else {
				const newAffiliateUser = await subCollectionCreate(AffiliateUser.collectionName, this.affiliateId, AffiliateUser.subCollectionName, this.userId, this.toDto());
				if (isObjectNotEmpty(newAffiliateUser)) {
					await AuthUser.updateAffiliateClaims(this.affiliateId, this.userId, this.roles);

					return newAffiliateUser;
				}
			}
		}

		return null;
	}

	static update = async (affiliateId, userId, updatedAffiliateUser) => {
		const updatedObj = new AffiliateUser(updatedAffiliateUser);
		if (updatedObj.isValid() && affiliateId === updatedObj.affiliateId && userId === updatedObj.userId) {
			const success = await subCollectionUpdate(AffiliateUser.collectionName, updatedObj.affiliateId, AffiliateUser.subCollectionName, updatedObj.userId, updatedObj.toDto());
			if (isBooleanTrue(success)) {
				await AuthUser.updateAffiliateClaims(updatedObj.affiliateId, updatedObj.userId, updatedObj.roles);

				return true;
			}
		} else {
			throw new Error('affiliateId and userId must match that of the updated affiliate user.')
		}

		return false;
	}

	static delete = async (affiliateId, userId) => {
		return await subCollectionRemove(AffiliateUser.collectionName, affiliateId, AffiliateUser.subCollectionName, userId);
	}

	static getByAffiliateIdAndUserId = async (affiliateId, userId) => {
		return await subCollectionGetByParentIdAndSubId(AffiliateUser.collectionName, affiliateId, AffiliateUser.subCollectionName, userId);
	}

	static getByUserId = async (userId) => {
		return await subCollectionGetBySingleQuery(AffiliateUser.subCollectionName, 'userId', userId, '==');
	}

	static getByQueryConstraints = async (queryConstraints = []) => {
		let q = query(collectionGroup(db, AffiliateUser.subCollectionName), ...queryConstraints);

		const snapShot = await getDocs(q);
		// console.log(snapShot)
		return snapShot.docs.map((doc) => {
			return {
				id: doc.id, // append document id to each document
				...doc.data()
			};
		});
	}

	static getByPhone = async (phone) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(phone)) {
			const cleanPhone = toPhoneNumberLocal(phone);
			const formattedPhone = toE164PhoneNumber(phone);
			queryConstraints.push(where('phone', 'in', [phone, formattedPhone, cleanPhone]));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByEmail = async (email) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(email)) {
			queryConstraints.push(where('email', '==', toLower(email)));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByAffiliateIdAndPhone = async (affiliateId, phone) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(affiliateId)) {
			queryConstraints.push(where('affiliateId', '==', affiliateId));
		}
		if (isStringNotEmptyOrZeroLength(phone)) {
			const cleanPhone = toPhoneNumberLocal(phone);
			const formattedPhone = toE164PhoneNumber(phone);
			queryConstraints.push(where('phone', 'in', [phone, formattedPhone, cleanPhone]));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByAffiliateIdAndEmail = async (affiliateId, email) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(affiliateId)) {
			queryConstraints.push(where('affiliateId', '==', affiliateId));
		}
		if (isStringNotEmptyOrZeroLength(email)) {
			queryConstraints.push(where('email', '==', toLower(email)));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getByAffiliateIdAndFirstNameAndLastNameAndEmailAndPhone = async (affiliateId, firstName, lastName, email, phone) => {
		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(affiliateId)) {
			queryConstraints.push(where('affiliateId', '==', affiliateId));
		}
		if (isStringNotEmptyOrZeroLength(firstName)) {
			queryConstraints.push(where('firstNameLower', '==', toLower(firstName)));
		}
		if (isStringNotEmptyOrZeroLength(lastName)) {
			queryConstraints.push(where('lastNameLower', '==', toLower(lastName)));
		}
		if (isStringNotEmptyOrZeroLength(email)) {
			queryConstraints.push(where('email', '==', toLower(email)));
		}
		if (isStringNotEmptyOrZeroLength(phone)) {
			const cleanPhone = toPhoneNumberLocal(phone);
			const formattedPhone = toE164PhoneNumber(phone);
			queryConstraints.push(where('phone', 'in', [phone, formattedPhone, cleanPhone]));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static getCount = async (queryParameters) => {
		const { affiliateId, role } = queryParameters;

		let queryConstraints = [];

		if (isStringNotEmptyOrZeroLength(affiliateId)) {
			queryConstraints.push(where('affiliateId', '==', affiliateId));
		}

		if (isListNotEmpty(role)) {
			queryConstraints.push(where('roles', 'array-contains-any', role));
		} else if (isStringNotEmptyOrZeroLength(role)) {
			queryConstraints.push(where('roles', 'array-contains', role));
		}


		let q = query(collectionGroup(db, AffiliateUser.subCollectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);
		//console.log(snapShot.data());

		return snapShot.data().count;
	}

	static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
		const { affiliateId, role } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(affiliateId)) {
			queryConstraints.push(where('affiliateId', 'in', affiliateId));
		} else if (isStringNotEmptyOrZeroLength(affiliateId)) {
			queryConstraints.push(where('affiliateId', '==', affiliateId));
		}

		if (isListNotEmpty(role)) {
			queryConstraints.push(where('roles', 'array-contains-any', role));
		} else if (isStringNotEmptyOrZeroLength(role)) {
			queryConstraints.push(where('roles', 'array-contains', role));
		}

		queryConstraints.push(orderBy(orderByField, 'asc'));

		if (isStringNotEmpty(firstVisible)) {
			queryConstraints.push(limitToLast(pageSize));
			queryConstraints.push(endBefore(lastVisible[orderByField]));
		} else if (isStringNotEmpty(lastVisible)) {
			queryConstraints.push(limit(pageSize));
			queryConstraints.push(startAfter(lastVisible[orderByField]));
		} else {
			queryConstraints.push(limit(pageSize));
		}

		return await this.getByQueryConstraints(queryConstraints);
	}

	static get = async (queryParameters) => {
		let allRecords = [];
		try {
			let next = null;

			let totalCount = await this.getCount(queryParameters);
			let totalPages = Math.ceil(totalCount / 10000.0);
			// console.log('totalCount: ' + totalCount);
			// console.log('totalPages: ' + totalPages);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				// console.log('starting page ' + page);

				let list = await this.getPaginated(queryParameters, 10000, null, next, 'createdAt');

				// console.log(list.length);
				allRecords = [...allRecords, ...list];

				// console.log(allRecords.length);

				// console.log('go to next page');
				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
		}

		return allRecords;
	}
}

export default AffiliateUser;